import React, { useEffect, useState } from 'react';
import {
  Container,
  ContentHeader,
  Title,
  Subtitle,
  SettingsCard,
  SettingsTitle,
  SettingsDescription,
  RadioGroupContainer,
  HelperText,
  FiltersAndStatsContainer,
  FilterRow,
  StatusFilterContainer,
  StatusPill,
  StatusSummaryContainer,
  StatusSummaryChip,
  StatusSummaryLabel,
  StatusSummaryCount,
  TableCard,
  TableHeader,
  TableTitle,
  ContentBody,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PageSizeControl,
  StatusBadge,
  RestaurantCardsContainer,
  RestaurantCard,
  RestaurantHeader,
  RestaurantName,
  RestaurantLocation,
  RestaurantDetails,
  DetailRow,
  DetailLabel,
  DetailValue,
  StatusContainer,
  CardActionsContainer,
  ActionButton,
  EmptyStateContainer,
  EmptyStateText
} from './RestaurantApproval.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchApprovalMode,
  updateApprovalMode,
  fetchRestaurantApprovals,
  approveRestaurant,
  rejectRestaurant,
  selectRestaurantApprovals,
  selectRowCount,
  selectPageSize,
  selectCurrentPage,
  selectCurrentStatus,
  selectApprovalMode,
  selectSeverity,
  selectMessage,
  selectShouldOpen,
  selectIsLoading,
  setPageSize,
  setCurrentPage,
  setCurrentStatus,
  setOpen
} from './slice';
import PageContainer from '../../components/PageContainer/PageContainer';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { RestaurantApprovalListItemDTO } from './types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const RestaurantApproval: React.FC = () => {
  const dispatch = useAppDispatch();
  const restaurants = useAppSelector(selectRestaurantApprovals);
  const rowCount = useAppSelector(selectRowCount);
  const pageSize = useAppSelector(selectPageSize);
  const currentPage = useAppSelector(selectCurrentPage);
  const currentStatus = useAppSelector(selectCurrentStatus);
  const approvalMode = useAppSelector(selectApprovalMode);
  const isLoading = useAppSelector(selectIsLoading);
  const openServer = useAppSelector(selectShouldOpen);
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [adminAccessLevel, setAdminAccessLevel] = useState('');

  useEffect(() => {
    void dispatch(fetchApprovalMode());
  }, [dispatch]);

  useEffect(() => {
    void dispatch(
      fetchRestaurantApprovals({
        page: currentPage,
        size: pageSize,
        status: currentStatus
      })
    );
  }, [dispatch, currentPage, pageSize, currentStatus]);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  const handleApprovalModeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newMode = event.target.value as 'AUTO' | 'MANUAL';
    void dispatch(updateApprovalMode(newMode));
  };

  const handleStatusChange = (event: any): void => {
    dispatch(setCurrentStatus(event.target.value as 'PENDING' | 'AUTO_APPROVED' | 'MANUAL_REVIEW_REQUIRED' | 'REJECTED'));
  };

  const handlePageSizeChange = (event: any): void => {
    dispatch(setPageSize(event.target.value));
  };

  const handleNextPage = (): void => {
    const totalPages = Math.ceil(rowCount / pageSize);
    if (currentPage < totalPages - 1) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePreviousPage = (): void => {
    if (currentPage > 0) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  const handleApproveClick = (restaurantId: string): void => {
    setSelectedRestaurantId(restaurantId);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = (): void => {
    if (selectedRestaurantId !== null) {
      void dispatch(
        approveRestaurant({
          restaurantId: selectedRestaurantId,
          adminAccessLevel: adminAccessLevel.length > 0 ? adminAccessLevel : undefined
        })
      ).then(() => {
        setApproveDialogOpen(false);
        setSelectedRestaurantId(null);
        setAdminAccessLevel('');
        void dispatch(
          fetchRestaurantApprovals({
            page: currentPage,
            size: pageSize,
            status: currentStatus
          })
        );
      });
    }
  };

  const handleRejectClick = (restaurantId: string): void => {
    setSelectedRestaurantId(restaurantId);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = (): void => {
    if (selectedRestaurantId !== null) {
      void dispatch(
        rejectRestaurant({
          restaurantId: selectedRestaurantId,
          reason: rejectReason.length > 0 ? rejectReason : undefined
        })
      ).then(() => {
        setRejectDialogOpen(false);
        setSelectedRestaurantId(null);
        setRejectReason('');
        void dispatch(
          fetchRestaurantApprovals({
            page: currentPage,
            size: pageSize,
            status: currentStatus
          })
        );
      });
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'Pending';
      case 'AUTO_APPROVED':
        return 'Auto-Approved';
      case 'MANUAL_APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | undefined): string => {
    if (dateString === undefined || dateString === '') return 'N/A';
    try {
      return dayjs(dateString).fromNow();
    } catch {
      return dateString;
    }
  };

  const totalPages = Math.ceil(rowCount / pageSize);
  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, rowCount);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  return (
    <PageContainer
      title="Restaurant Approvals"
      open={openServer}
      setOpen={setOpenServer}
      message={message}
      severity={severity}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container>
        <ContentHeader>
          <Title>Restaurant Approvals</Title>
          <Subtitle>Review and manage restaurant partners before they go live.</Subtitle>
        </ContentHeader>

        {/* Global Approval Settings */}
        <SettingsCard>
          <SettingsTitle>Approval Mode</SettingsTitle>
          <SettingsDescription>
            Control whether new restaurants are automatically approved or must be reviewed by an admin.
          </SettingsDescription>
          <RadioGroupContainer>
            <FormControl component="fieldset">
              <RadioGroup
                value={approvalMode ?? 'MANUAL'}
                onChange={handleApprovalModeChange}
              >
                <FormControlLabel
                  value="AUTO"
                  control={<Radio />}
                  label="Auto-approve new restaurants"
                />
                <FormControlLabel
                  value="MANUAL"
                  control={<Radio />}
                  label="Require manual approval"
                />
              </RadioGroup>
            </FormControl>
          </RadioGroupContainer>
          <HelperText>Changes apply to future sign-ups only.</HelperText>
        </SettingsCard>

        {/* Filters and Status Summary */}
        <FiltersAndStatsContainer>
          <FilterRow>
            <StatusFilterContainer>
              <StatusPill
                isActive={currentStatus === 'PENDING'}
                onClick={() => handleStatusChange({ target: { value: 'PENDING' } })}
              >
                PENDING
              </StatusPill>
              <StatusPill
                isActive={currentStatus === 'AUTO_APPROVED'}
                onClick={() => handleStatusChange({ target: { value: 'AUTO_APPROVED' } })}
              >
                AUTO_APPROVED
              </StatusPill>
              <StatusPill
                isActive={currentStatus === 'MANUAL_REVIEW_REQUIRED'}
                onClick={() => handleStatusChange({ target: { value: 'MANUAL_REVIEW_REQUIRED' } })}
              >
                MANUAL REVIEW
              </StatusPill>
              <StatusPill
                isActive={currentStatus === 'REJECTED'}
                onClick={() => handleStatusChange({ target: { value: 'REJECTED' } })}
              >
                REJECTED
              </StatusPill>
            </StatusFilterContainer>
            <StatusSummaryContainer>
              <StatusSummaryChip type="pending">
                <StatusSummaryLabel>Pending</StatusSummaryLabel>
                <StatusSummaryCount>
                  {restaurants.filter((r) => r.approvalStatus === 'PENDING').length}
                </StatusSummaryCount>
              </StatusSummaryChip>
              <StatusSummaryChip type="approved">
                <StatusSummaryLabel>Approved</StatusSummaryLabel>
                <StatusSummaryCount>
                  {restaurants.filter((r) => r.approvalStatus === 'AUTO_APPROVED' || r.approvalStatus === 'MANUAL_APPROVED').length}
                </StatusSummaryCount>
              </StatusSummaryChip>
              <StatusSummaryChip type="rejected">
                <StatusSummaryLabel>Rejected</StatusSummaryLabel>
                <StatusSummaryCount>
                  {restaurants.filter((r) => r.approvalStatus === 'REJECTED').length}
                </StatusSummaryCount>
              </StatusSummaryChip>
            </StatusSummaryContainer>
          </FilterRow>
        </FiltersAndStatsContainer>

        {/* Restaurant Cards */}
        <ContentBody>
          <TableCard>
            <TableHeader>
              <TableTitle>Restaurants</TableTitle>
            </TableHeader>

            {restaurants.length > 0 ? (
              <RestaurantCardsContainer>
                {restaurants.map((restaurant: RestaurantApprovalListItemDTO) => (
                  <RestaurantCard key={restaurant.restaurantId}>
                    <RestaurantHeader>
                      <RestaurantName>{restaurant.name}</RestaurantName>
                      <RestaurantLocation>
                        {restaurant.city} {restaurant.zipCode}
                      </RestaurantLocation>
                    </RestaurantHeader>

                    <RestaurantDetails>
                      <DetailRow>
                        <DetailLabel>Contact Email</DetailLabel>
                        <DetailValue>{restaurant.contactEmail}</DetailValue>
                      </DetailRow>

                      {restaurant.adminAccessLevel !== undefined && restaurant.adminAccessLevel !== '' && (
                        <DetailRow>
                          <DetailLabel>Admin Access Level</DetailLabel>
                          <DetailValue>{restaurant.adminAccessLevel}</DetailValue>
                        </DetailRow>
                      )}

                      <DetailRow>
                        <DetailLabel>Created</DetailLabel>
                        <DetailValue>{formatDate(restaurant.created)}</DetailValue>
                      </DetailRow>

                      <StatusContainer>
                        <DetailLabel>Status</DetailLabel>
                        <StatusBadge status={restaurant.approvalStatus}>
                          {getStatusLabel(restaurant.approvalStatus)}
                        </StatusBadge>
                      </StatusContainer>
                    </RestaurantDetails>

                    {(restaurant.approvalStatus === 'PENDING' || restaurant.approvalStatus === 'MANUAL_REVIEW_REQUIRED') && (
                      <CardActionsContainer>
                        <ActionButton
                          variant="contained"
                          color="success"
                          onClick={() => handleApproveClick(restaurant.restaurantId)}
                        >
                          Approve
                        </ActionButton>
                        <ActionButton
                          variant="outlined"
                          color="error"
                          onClick={() => handleRejectClick(restaurant.restaurantId)}
                        >
                          Reject
                        </ActionButton>
                      </CardActionsContainer>
                    )}
                  </RestaurantCard>
                ))}
              </RestaurantCardsContainer>
            ) : (
              <EmptyStateContainer>
                <EmptyStateText>No restaurants found</EmptyStateText>
                <EmptyStateText style={{ fontSize: '12px', fontWeight: 400 }}>
                {`There are no ${currentStatus === 'AUTO_APPROVED' ? 'auto-approved' : currentStatus === 'MANUAL_REVIEW_REQUIRED' ? 'manual review required' : currentStatus.toLowerCase()} restaurants.`}
              </EmptyStateText>
              </EmptyStateContainer>
            )}

            {rowCount > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {rowCount > 0 ? startIndex : 0} to {endIndex} of {rowCount} restaurants
                </PaginationInfo>
                <PageSizeControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Per Page</InputLabel>
                    <Select value={pageSize} label="Per Page" onChange={handlePageSizeChange}>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </PageSizeControl>
                <PaginationControls>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePreviousPage}
                    disabled={!hasPreviousPage}
                  >
                    Previous
                  </Button>
                  <span>
                    {currentPage + 1} of {totalPages > 0 ? totalPages : 1}
                  </span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                  >
                    Next
                  </Button>
                </PaginationControls>
              </PaginationContainer>
            )}
          </TableCard>
        </ContentBody>

        {/* Approve Dialog */}
        <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
          <DialogTitle>Approve Restaurant</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Approve this restaurant. You can optionally set an admin access level.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Admin Access Level (optional)"
              fullWidth
              variant="outlined"
              value={adminAccessLevel}
              onChange={(e) => setAdminAccessLevel(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApproveConfirm} variant="contained" color="success">
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
          <DialogTitle>Reject Restaurant</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Reject this restaurant. You can optionally provide a reason.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Reason (optional)"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRejectConfirm} variant="contained" color="error">
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageContainer>
  );
};

export default withAuthenticationRequired(RestaurantApproval);
